/**
 * NotificationPanel — Enterprise Design System
 *
 * Dropdown notification panel with tabbed filtering.
 * Wraps/extends the existing notification system with a design-system-consistent UI.
 *
 * Features:
 *   - Icon button with unread count badge (hidden at 0, "99+" for >99)
 *   - Popover panel listing 50 most recent notifications sorted by creation time desc
 *   - Tab bar filters: All (default), System, Approval, Task, Alert
 *   - Each notification: type icon, title, description (2-line truncation), relative time, read/unread indicator
 *   - Click linked notification → navigate + mark read
 *   - Click unlinked notification → mark read only
 *   - "Mark All as Read" action in panel header
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Button,
  Avatar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications,
  NotificationsNone,
  DoneAll,
  Info,
  CheckCircle,
  Assignment,
  Warning,
  ConfirmationNumber,
  Build,
  Backup,
  Business,
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import {
  COLOR_PRIMARY,
  COLOR_DANGER,
  COLOR_SUCCESS,
  COLOR_WARNING,
  COLOR_INFO,
  COLOR_NEUTRAL,
  RADIUS_LG,
  SHADOW,
  COLOR_BORDER,
} from '../theme/tokens';

// ── Category definitions for tab filtering ─────────────────────────────────────
const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'system', label: 'System' },
  { key: 'approval', label: 'Approval' },
  { key: 'task', label: 'Task' },
  { key: 'alert', label: 'Alert' },
];

// Map notification types to categories
const TYPE_TO_CATEGORY = {
  system: 'system',
  ticket_created: 'task',
  ticket_assigned: 'task',
  ticket_updated: 'task',
  ticket_resolved: 'task',
  ticket_sla_breach: 'alert',
  asset_warranty_expiry: 'alert',
  maintenance_due: 'task',
  backup_failed: 'alert',
  backup_completed: 'system',
  vendor_expiry: 'alert',
  approval_required: 'approval',
  approval_granted: 'approval',
  approval_rejected: 'approval',
};

// ── Type → icon + colour ───────────────────────────────────────────────────────
const TYPE_META = {
  ticket_created: { icon: <ConfirmationNumber sx={{ fontSize: 16 }} />, color: COLOR_PRIMARY, bg: '#eff6ff' },
  ticket_assigned: { icon: <Assignment sx={{ fontSize: 16 }} />, color: '#7c3aed', bg: '#f5f3ff' },
  ticket_updated: { icon: <ConfirmationNumber sx={{ fontSize: 16 }} />, color: COLOR_INFO, bg: '#eff6ff' },
  ticket_resolved: { icon: <CheckCircle sx={{ fontSize: 16 }} />, color: COLOR_SUCCESS, bg: '#ecfdf5' },
  ticket_sla_breach: { icon: <Warning sx={{ fontSize: 16 }} />, color: COLOR_DANGER, bg: '#fef2f2' },
  asset_warranty_expiry: { icon: <Build sx={{ fontSize: 16 }} />, color: COLOR_WARNING, bg: '#fffbeb' },
  maintenance_due: { icon: <Build sx={{ fontSize: 16 }} />, color: COLOR_WARNING, bg: '#fffbeb' },
  backup_failed: { icon: <Backup sx={{ fontSize: 16 }} />, color: COLOR_DANGER, bg: '#fef2f2' },
  backup_completed: { icon: <Backup sx={{ fontSize: 16 }} />, color: COLOR_SUCCESS, bg: '#ecfdf5' },
  vendor_expiry: { icon: <Business sx={{ fontSize: 16 }} />, color: COLOR_WARNING, bg: '#fffbeb' },
  approval_required: { icon: <Assignment sx={{ fontSize: 16 }} />, color: COLOR_PRIMARY, bg: '#eff6ff' },
  approval_granted: { icon: <CheckCircle sx={{ fontSize: 16 }} />, color: COLOR_SUCCESS, bg: '#ecfdf5' },
  approval_rejected: { icon: <Warning sx={{ fontSize: 16 }} />, color: COLOR_DANGER, bg: '#fef2f2' },
  system: { icon: <Info sx={{ fontSize: 16 }} />, color: COLOR_NEUTRAL, bg: '#f1f5f9' },
};

// ── Relative time formatter ────────────────────────────────────────────────────
function timeAgo(date) {
  const diff = Date.now() - new Date(date).getTime();
  const m = Math.floor(diff / 60_000);
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

// ── Badge display helper ───────────────────────────────────────────────────────
/**
 * Formats unread count for badge display.
 * Returns null for 0, numeric string for 1-99, "99+" for >99.
 */
export function formatBadgeCount(count) {
  if (count <= 0) return null;
  if (count > 99) return '99+';
  return count;
}

// ── NotificationItem sub-component ────────────────────────────────────────────
function NotificationItem({ notif, onRead, onNavigate }) {
  const meta = TYPE_META[notif.type] || TYPE_META.system;

  const handleClick = () => {
    if (!notif.isRead) onRead(notif._id);
    if (notif.link) onNavigate(notif.link);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        bgcolor: notif.isRead ? 'transparent' : '#eff6ff',
        borderLeft: notif.isRead
          ? '3px solid transparent'
          : `3px solid ${meta.color}`,
        '&:hover': { bgcolor: 'grey.50' },
        transition: 'background .15s',
      }}
    >
      {/* Icon avatar */}
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: meta.bg,
          color: meta.color,
          flexShrink: 0,
        }}
      >
        {meta.icon}
      </Avatar>

      {/* Content */}
      <Box flex={1} minWidth={0}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography
            variant="body2"
            fontWeight={notif.isRead ? 400 : 700}
            noWrap
            sx={{ flex: 1, mr: 1 }}
          >
            {notif.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" flexShrink={0}>
            {timeAgo(notif.createdAt)}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {notif.message}
        </Typography>
      </Box>

      {/* Read/unread indicator dot */}
      {!notif.isRead && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: COLOR_PRIMARY,
            flexShrink: 0,
            mt: 0.5,
          }}
        />
      )}
    </Box>
  );
}

// ── Main NotificationPanel component ──────────────────────────────────────────
export default function NotificationPanel() {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    refresh,
  } = useNotifications();

  const [anchor, setAnchor] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = All

  const open = Boolean(anchor);

  const handleOpen = useCallback((e) => {
    setAnchor(e.currentTarget);
    setActiveTab(0); // Default to "All" when opened
    refresh();
  }, [refresh]);

  const handleClose = useCallback(() => {
    setAnchor(null);
  }, []);

  const handleNavigate = useCallback((link) => {
    setAnchor(null);
    navigate(link);
  }, [navigate]);

  const handleTabChange = useCallback((_event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleMarkAllRead = useCallback(() => {
    markAllRead();
  }, [markAllRead]);

  // Filter notifications by active tab category, take top 50, sort by creation time desc
  const filteredNotifications = useMemo(() => {
    const categoryKey = CATEGORIES[activeTab]?.key || 'all';

    let filtered = notifications;
    if (categoryKey !== 'all') {
      filtered = notifications.filter((notif) => {
        const mappedCategory = TYPE_TO_CATEGORY[notif.type] || 'system';
        return mappedCategory === categoryKey;
      });
    }

    // Sort by creation time descending and take top 50
    return [...filtered]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 50);
  }, [notifications, activeTab]);

  const badgeContent = formatBadgeCount(unreadCount);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={handleOpen} size="small" sx={{ mr: 0.25 }}>
          <Badge
            badgeContent={badgeContent}
            color="error"
            invisible={badgeContent === null}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.625rem',
                fontWeight: 700,
                minWidth: 18,
                height: 18,
                padding: '0 5px',
              },
            }}
          >
            {unreadCount > 0 ? (
              <Notifications sx={{ color: 'text.primary' }} />
            ) : (
              <NotificationsNone sx={{ color: 'text.secondary' }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 560,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: RADIUS_LG,
            boxShadow: SHADOW[16],
            border: `1px solid ${COLOR_BORDER}`,
            mt: 1,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <Button
                size="small"
                startIcon={<DoneAll fontSize="small" />}
                onClick={handleMarkAllRead}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Mark All as Read
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Tab bar */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 36,
            px: 1,
            '& .MuiTab-root': {
              minHeight: 36,
              textTransform: 'none',
              fontSize: '0.8rem',
              py: 0,
              px: 1.5,
            },
          }}
        >
          {CATEGORIES.map((cat) => (
            <Tab key={cat.key} label={cat.label} />
          ))}
        </Tabs>

        <Divider />

        {/* Notification list */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && filteredNotifications.length === 0 && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={4}
              gap={1}
            >
              <NotificationsNone sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          )}

          {!loading &&
            filteredNotifications.map((notif, i) => (
              <React.Fragment key={notif._id}>
                <NotificationItem
                  notif={notif}
                  onRead={markRead}
                  onNavigate={handleNavigate}
                />
                {i < filteredNotifications.length - 1 && (
                  <Divider sx={{ mx: 2 }} />
                )}
              </React.Fragment>
            ))}
        </Box>

        {/* Footer */}
        {filteredNotifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'center' }}>
              <Button
                size="small"
                onClick={() => {
                  handleClose();
                  navigate('/notifications');
                }}
                sx={{ textTransform: 'none' }}
              >
                View all notifications
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
